import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import Cookies from 'js-cookie'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'

import { navigateAddModal, navigateRemoveModal, setNavigationPath } from '../../actions/navigation'
import { l } from '../../Other/Translation'
import Button, { ButtonType } from '../common/Button/Button'
import CheckBox from '../common/CheckBox/CheckBox'
import { Modal } from '../common/Modal/Modal'
import { isInvoiceDraftOrUnsaved } from './utils/isInvoiceDraftOrUnsaved'

const INVOICE_CONFIRMATION_COOKIE = 'donNotShowInvoiceConfirmationModal'

interface HandleInvoiceConfirmationModalProps {
  dispatch: Dispatch<any>
  onApproveInvoice: (shouldSkipModal?: Boolean) => void | Promise<void>
  onOpen?: () => void
  invoiceId?: number
  documentType?: DocumentTypes
}

export const handleInvoiceConfirmationModal = async ({
  dispatch,
  onApproveInvoice,
  onOpen,
  invoiceId,
  documentType,
}: HandleInvoiceConfirmationModalProps) => {
  const isInvoiceDraft = await isInvoiceDraftOrUnsaved(invoiceId, documentType)
  const shouldSkipModal = !isInvoiceDraft || Cookies.get(INVOICE_CONFIRMATION_COOKIE) === 'true'

  if (shouldSkipModal) {
    await onApproveInvoice(shouldSkipModal)
  } else {
    dispatch(
      navigateAddModal({
        component: 'InvoiceConfirmationModal',
        props: {
          onApproveInvoice,
          onOpen,
        },
      }),
    )
    return Promise.resolve()
  }
}

const InvoiceConfirmationModal = ({ onApproveInvoice, onOpen = () => {} }) => {
  const dispatch = useDispatch()
  const [doNotShowAgain, setDoNotShowAgain] = React.useState(false)
  const [isProcessing, setIsProcessing] = React.useState(false)

  const closeModal = () => {
    dispatch(navigateRemoveModal('InvoiceConfirmationModal'))
  }

  const handleDoNotShowAgainSelection = () => {
    if (doNotShowAgain) {
      Cookies.set(INVOICE_CONFIRMATION_COOKIE, 'true', { expires: 365 * 10 })
    }
  }

  const handleApproveInvoiceClick = async () => {
    setIsProcessing(true)
    handleDoNotShowAgainSelection()
    await onApproveInvoice()
    setIsProcessing(false)
    closeModal()
  }

  const handleEditInvoiceClick = () => {
    closeModal()
    handleDoNotShowAgainSelection()
    dispatch(setNavigationPath('/reactInvoice'))
  }

  React.useEffect(() => {
    if (onOpen) {
      onOpen()
    }
  }, [])

  return (
    <Modal
      size="medium"
      isOpen={true}
      showCloseIcon={true}
      className="InvoiceConfirmationModal"
      header={l('InvoiceConfirmation.header')}
      onClose={handleDoNotShowAgainSelection}
    >
      <figure className="icon">
        <img src="/images/icon_invoice_approval.svg" />
      </figure>
      <h2 className="title">{l('InvoiceConfirmation.title')}</h2>
      <div className="text-content">
        <p className="header-text">{l('InvoiceConfirmation.headerText')}</p>
        <hr />

        <ul className="details-list">
          <li className="detail-item">{l('InvoiceConfirmation.detailsList.item1')}</li>
          <li className="detail-item">{l('InvoiceConfirmation.detailsList.item2')}</li>
          <li className="detail-item">{l('InvoiceConfirmation.detailsList.item3')}</li>
          <li className="detail-item">{l('InvoiceConfirmation.detailsList.item4')}</li>
        </ul>
        <p className="details-list-footer">{l('InvoiceConfirmation.detailsList.footer')}</p>
      </div>

      <footer>
        <Button
          type={ButtonType.accept}
          action={handleApproveInvoiceClick}
          automation={`-approveBtn`}
          label={l('InvoiceConfirmation.button.approve')}
          showLoadingAnimation={isProcessing}
        />
        <Button
          type={ButtonType.cancel}
          action={handleEditInvoiceClick}
          automation={`-editBtn`}
          label={l('InvoiceConfirmation.button.edit')}
        />
        <hr />
        <CheckBox
          label={l('InvoiceConfirmation.checkbox.label.dontShowAgain')}
          value={doNotShowAgain}
          data-automation="invoice-approval-do-not-show-again"
          onChange={() => {
            setDoNotShowAgain(!doNotShowAgain)
          }}
        />
      </footer>
    </Modal>
  )
}

export default InvoiceConfirmationModal
